import { useState } from "react";
import Reveal from "@/components/landing/Reveal";
import { WEB3FORMS_KEY } from "@/lib/content";

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        suburb: "",
        project: "",
    });
    const [status, setStatus] = useState("idle"); // idle | sending | sent | error
    const [errorMsg, setErrorMsg] = useState("");

    const upd = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        setErrorMsg("");
        try {
            // Use URLSearchParams to send as application/x-www-form-urlencoded
            // This avoids CORS preflight (simple request)
            const params = new URLSearchParams();
            params.append("access_key", WEB3FORMS_KEY);
            params.append("subject", `New enquiry from ${form.name || "the Arc & Light website"}`);
            params.append("from_name", "Arc & Light — Website");
            params.append("replyto", form.email);
            params.append("botcheck", "");
            params.append("Name", form.name);
            params.append("Email", form.email);
            params.append("Phone", form.phone || "—");
            params.append("Suburb", form.suburb || "—");
            params.append("Project", form.project || "—");

            const res = await fetch("https://api.web3forms.com/submit", {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
                body: params.toString(),
            });
            const json = await res.json();
            if (json.success) {
                setStatus("sent");
                setForm({ name: "", email: "", phone: "", suburb: "", project: "" });
            } else {
                setStatus("error");
                setErrorMsg(json.message || "Something went wrong. Please try again.");
            }
        } catch (err) {
            setStatus("error");
            setErrorMsg("Network error. Please try again, or email merilyn@merilyn.com.au directly.");
        }
    };

    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative bg-cacao text-pearl py-32 md:py-44 overflow-hidden"
        >
            <div className="grain" aria-hidden="true" />
            <div className="max-w-[1200px] mx-auto px-6 md:px-12 relative">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
                    <div className="lg:col-span-5">
                        <Reveal>
                            <p className="eyebrow text-pearl/60 mb-8">
                                <span className="divider-line bg-pearl/40 mr-4 align-middle" />
                                Begin here
                            </p>
                        </Reveal>
                        <Reveal delay={120}>
                            <h2
                                data-testid="contact-headline"
                                className="font-serif text-pearl text-[2.4rem] sm:text-[3.2rem] md:text-[4rem] leading-[1.05]"
                            >
                                {"Let\u2019s begin"}
                                <br />
                                {"with a "}
                                <em className="italic text-clay">{"conversation."}</em>
                            </h2>
                        </Reveal>
                        <Reveal delay={240}>
                            <p className="mt-10 text-pearl/80 text-[1rem] leading-[1.85] font-light max-w-[28rem]">
                                {"Tell us a little about your home and what you\u2019d love it to feel like. Merilyn will be in touch personally within one business day."}
                            </p>
                        </Reveal>

                        <Reveal delay={360}>
                            <div className="mt-14 space-y-3 text-pearl/70 text-[0.85rem] tracking-[0.04em]">
                                <img
                                    src="/logos/AandL-white.png"
                                    alt=""
                                    aria-hidden="true"
                                    width="32"
                                    height="10"
                                    className="h-6 md:h-7 w-auto opacity-80 select-none block mb-4"
                                    draggable="false"
                                />
                                <p className="font-serif italic text-pearl/85 text-[1.05rem]">
                                    merilyn@merilyn.com.au
                                </p>
                                <p className="text-[0.72rem] tracking-[0.28em] uppercase text-pearl/55">
                                    Brisbane · Inner East &amp; Riverside
                                </p>
                            </div>
                        </Reveal>
                    </div>

                    <div className="lg:col-span-7">
                        <Reveal delay={200}>
                            <form
                                onSubmit={onSubmit}
                                data-testid="contact-form"
                                className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3"
                            >
                                <input
                                    type="text"
                                    placeholder="Name"
                                    value={form.name}
                                    onChange={upd("name")}
                                    className="field-line on-dark"
                                    data-testid="contact-name"
                                    required
                                />
                                <input
                                    type="email"
                                    placeholder="Email"
                                    value={form.email}
                                    onChange={upd("email")}
                                    className="field-line on-dark"
                                    data-testid="contact-email"
                                    required
                                />
                                <input
                                    type="tel"
                                    placeholder="Phone (optional)"
                                    value={form.phone}
                                    onChange={upd("phone")}
                                    className="field-line on-dark"
                                    data-testid="contact-phone"
                                />
                                <input
                                    type="text"
                                    placeholder="Suburb (optional)"
                                    value={form.suburb}
                                    onChange={upd("suburb")}
                                    className="field-line on-dark"
                                    data-testid="contact-suburb"
                                />
                                <textarea
                                    placeholder="Tell us about your project"
                                    value={form.project}
                                    onChange={upd("project")}
                                    rows={4}
                                    className="field-line on-dark sm:col-span-2 resize-none"
                                    data-testid="contact-project"
                                />

                                <div className="sm:col-span-2 mt-10 flex flex-col sm:flex-row sm:items-center gap-6">
                                    <button
                                        type="submit"
                                        disabled={status === "sending" || status === "sent"}
                                        className="btn-clay disabled:opacity-60 disabled:cursor-not-allowed"
                                        data-testid="contact-submit"
                                    >
                                        {status === "sending" ? "Sending…" : status === "sent" ? "Sent" : "Send Enquiry"}
                                    </button>
                                    <p className="text-pearl/60 text-[0.78rem] italic font-serif">
                                        {"No pressure, no obligation — just a calm first conversation."}
                                    </p>
                                </div>

                                {status === "sent" && (
                                    <p
                                        data-testid="contact-confirmation"
                                        className="sm:col-span-2 mt-8 font-serif italic text-clay text-[1.15rem]"
                                    >
                                        Thank you. Merilyn will be in touch personally within one business day.
                                    </p>
                                )}
                                {status === "error" && (
                                    <p
                                        data-testid="contact-error"
                                        className="sm:col-span-2 mt-8 font-serif italic text-clay text-[1rem]"
                                    >
                                        {errorMsg}
                                    </p>
                                )}
                            </form>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
}
